import { useTranslation } from "react-i18next";
// src/components/CollectionTypeSelector.jsx
//
// Sélecteur de type de collection.
//
// Deux modes strictement séparés — jamais mélangés :
//   isFoundation=false (défaut) : 3 cartes collectionneur (private / inalienable / deposit)
//   isFoundation=true           : 4 cartes Fondation (foundation_owned / foundation_founder /
//                                 foundation_donation / foundation_deposit)
//
// Props :
//   value        : { collection_type, legal_status, legal_notes, deposit: {...}, thirdParty: {...} }
//   onChange     : (updates) => void  — reçoit les modifications partielles
//   isFoundation : boolean (défaut false) — passé depuis CollectionEdit via profile.foundation_mode
//
import React from "react";

// ─── Options collectionneur (inchangées) ─────────────────────────────────────
const COLLECTOR_TYPE_OPTIONS = [
  {
    value: "private",
    title: "Collection privée",
    description: "Propriété pleine. Gestion libre, valorisation et cession autorisées.",
    icon: "◐",
  },
  {
    value: "inalienable",
    title: "Œuvres inaliénables",
    description: "Régime patrimonial protégé. Valorisation indicative, cession soumise à autorisation.",
    icon: "✦",
  },
  {
    value: "deposit",
    title: "Dépôt",
    description: "Œuvres prêtées par un tiers. Hors patrimoine, sous contrat.",
    icon: "◇",
  },
];

// ─── Options Fondation ────────────────────────────────────────────────────────
const FOUNDATION_TYPE_OPTIONS = [
  {
    value: "foundation_owned",
    title: "Propriété propre",
    description: "Œuvres acquises par la fondation. Valorisation active, gestion pleine.",
    icon: "◉",
    accentColor: "#2C5F4A",
  },
  {
    value: "foundation_founder",
    title: "Prêt du fondateur",
    description: "Collection personnelle du fondateur mise à disposition de la fondation.",
    icon: "◈",
    accentColor: "#7A5C00",
  },
  {
    value: "foundation_donation",
    title: "Donation / legs",
    description: "Œuvres reçues par donation ou legs. Peuvent être redistribuées vers des institutions.",
    icon: "✦",
    accentColor: "#8B4513",
  },
  {
    value: "foundation_deposit",
    title: "Dépôt institution",
    description: "Œuvres dont la fondation a la garde. Propriétaire : État, musée ou institution.",
    icon: "◇",
    accentColor: "#1C3A5E",
  },
];

// ─── Statuts juridiques collectionneur (inchangés) ───────────────────────────
const COLLECTOR_LEGAL_STATUS_OPTIONS = [
  { value: "a_clarifier",          label: "À clarifier" },
  { value: "tresor_national",      label: "Trésor National classé" },
  { value: "dation",               label: "Dation en paiement" },
  { value: "don_legs_inalienable", label: "Don / legs avec clause d'inaliénabilité" },
  { value: "autre",                label: "Autre régime" },
];

// ─── Statuts juridiques Fondation ────────────────────────────────────────────
const FOUNDATION_LEGAL_STATUS_OPTIONS = [
  { value: "a_clarifier",               label: "À clarifier" },
  { value: "etat_musees_de_france",     label: "État — Musées de France (DRAC / CMN)" },
  { value: "fondation_abritee",         label: "Fondation abritée (Fondation de France...)" },
  { value: "don_avec_reserve_usufruit", label: "Don avec réserve d'usufruit" },
  { value: "pret_artiste_vivant",       label: "Prêt d'un artiste vivant" },
  { value: "pret_institution",          label: "Prêt d'une institution" },
  { value: "autre",                     label: "Autre régime" },
];

// ─── Types de propriétaire (dépôt collectionneur, inchangé) ──────────────────
const OWNER_TYPE_OPTIONS = [
  { value: "a_clarifier",       label: "À clarifier" },
  { value: "private_collector", label: "Collectionneur privé" },
  { value: "institution",       label: "Institution (musée, fondation)" },
  { value: "family_succession", label: "Famille / succession" },
];

// ─── Types de tiers Fondation ─────────────────────────────────────────────────
const FOUNDATION_PARTY_TYPE_OPTIONS = [
  { value: "a_clarifier",     label: "À clarifier" },
  { value: "etat",            label: "État français" },
  { value: "musee",           label: "Musée public" },
  { value: "institution",     label: "Institution privée" },
  { value: "fondation",       label: "Autre fondation" },
  { value: "donateur_prive",  label: "Donateur / famille" },
  { value: "artiste",         label: "Artiste" },
  { value: "autre",           label: "Autre" },
];

// ─── Helpers style ────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  background: "#fff",
  border: "1px solid #E5E2DD",
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  color: "#6B6B6B",
  marginBottom: 6,
};

// ─── Composant principal ──────────────────────────────────────────────────────
export default function CollectionTypeSelector({
  value = {},
  onChange,
  isFoundation = false,
}) {
  const { t } = useTranslation();
  const collectionType = value.collection_type || (isFoundation ? "foundation_owned" : "private");
  const legalStatus    = value.legal_status || "";
  const legalNotes     = value.legal_notes  || "";
  const deposit        = value.deposit      || {};
  const thirdParty     = value.thirdParty   || {};

  // ── Changement de type ───────────────────────────────────────────────────
  const handleTypeChange = (newType) => {
    const updates = { collection_type: newType };

    if (isFoundation) {
      // Fondation : legal_status/notes utiles pour foundation_deposit et foundation_donation
      const needsLegal = newType === "foundation_deposit" || newType === "foundation_donation";
      if (!needsLegal) {
        updates.legal_status = null;
        updates.legal_notes  = null;
      } else if (!legalStatus) {
        updates.legal_status = "a_clarifier";
      }
      // thirdParty : utile pour foundation_deposit et foundation_founder
      const needsThirdParty = newType === "foundation_deposit" || newType === "foundation_founder";
      if (!needsThirdParty) {
        updates.thirdParty = null;
      } else if (!thirdParty.party_type) {
        updates.thirdParty = { ...thirdParty, party_type: "a_clarifier" };
      }
      updates.deposit = null; // jamais utilisé côté Fondation
    } else {
      // Collectionneur : logique inchangée
      if (newType !== "inalienable") {
        updates.legal_status = null;
        updates.legal_notes  = null;
      } else if (!legalStatus) {
        updates.legal_status = "a_clarifier";
      }
      if (newType !== "deposit") {
        updates.deposit = null;
      } else if (!deposit.owner_type) {
        updates.deposit = { ...deposit, owner_type: "a_clarifier" };
      }
      updates.thirdParty = null; // jamais utilisé côté collectionneur
    }

    onChange(updates);
  };

  const handleDepositChange     = (field, val) => onChange({ deposit:     { ...deposit,     [field]: val } });
  const handleThirdPartyChange  = (field, val) => onChange({ thirdParty:  { ...thirdParty,  [field]: val } });

  // ── Sélection du jeu d'options selon le mode ─────────────────────────────
  const typeOptions = (isFoundation ? FOUNDATION_TYPE_OPTIONS : COLLECTOR_TYPE_OPTIONS).map((opt) => ({
    ...opt,
    title: t(`round36.collectionTypeSelector.types.${opt.value}.title`),
    description: t(`round36.collectionTypeSelector.types.${opt.value}.description`),
  }));
  const legalStatusOptions = (isFoundation ? FOUNDATION_LEGAL_STATUS_OPTIONS : COLLECTOR_LEGAL_STATUS_OPTIONS).map((opt) => ({
    ...opt,
    label: t(`round36.collectionTypeSelector.legalStatuses.${opt.value}`),
  }));
  const ownerTypeOptions = OWNER_TYPE_OPTIONS.map((opt) => ({ ...opt, label: t(`round36.collectionTypeSelector.ownerTypes.${opt.value}`) }));
  const foundationPartyTypeOptions = FOUNDATION_PARTY_TYPE_OPTIONS.map((opt) => ({ ...opt, label: t(`round36.collectionTypeSelector.partyTypes.${opt.value}`) }));

  // ── Détection des panneaux conditionnels ─────────────────────────────────
  // Collectionneur
  const showInalineablePanel = !isFoundation && collectionType === "inalienable";
  const showDepositPanel     = !isFoundation && collectionType === "deposit";
  // Fondation
  const showFoundationLegal  = isFoundation && (collectionType === "foundation_deposit" || collectionType === "foundation_donation");
  const showThirdPartyPanel  = isFoundation && (collectionType === "foundation_deposit" || collectionType === "foundation_founder");

  return (
    <div style={{ fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif" }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 12 }}>
        {t("round36.collectionTypeSelector.label")}
      </label>

      {/* ─── Cartes radio ─── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isFoundation ? "200px" : "220px"}, 1fr))`,
          gap: 12,
          marginBottom: 24,
        }}
      >
        {typeOptions.map((opt) => {
          const isSelected   = collectionType === opt.value;
          const accentColor  = opt.accentColor || "#C9A961";
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleTypeChange(opt.value)}
              style={{
                textAlign: "left",
                padding: "16px 18px",
                background: isSelected ? "#F9F7F4" : "#fff",
                border: isSelected ? `1.5px solid ${accentColor}` : "1px solid #E5E2DD",
                cursor: "pointer",
                transition: "all 0.15s",
                position: "relative",
                outline: "none",
              }}
            >
              <div style={{ fontSize: 18, color: isSelected ? accentColor : "#A8A8A8", marginBottom: 6, fontFamily: "Georgia, serif" }}>
                {opt.icon}
              </div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: 15, color: "#1A1A1A", marginBottom: 4 }}>
                {opt.title}
              </div>
              <div style={{ fontSize: 11, color: "#6B6B6B", lineHeight: 1.4 }}>
                {opt.description}
              </div>
              {isSelected && (
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  width: 16, height: 16, borderRadius: "50%",
                  background: accentColor, color: "#fff",
                  fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  ✓
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PANNEAUX CONDITIONNELS — COLLECTIONNEUR (inchangés)
      ══════════════════════════════════════════════════════════════════════ */}

      {/* Inaliénable */}
      {showInalineablePanel && (
        <div style={{ background: "#FAF8F3", border: "1px solid #E5D5A8", padding: "20px 22px", marginBottom: 16 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1A1A1A", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#C9A961" }}>✦</span>
            {t("round36.collectionTypeSelector.panels.inalienableTitle")}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.legalStatus")}</label>
            <select
              value={legalStatus}
              onChange={(e) => onChange({ legal_status: e.target.value })}
              style={inputStyle}
            >
              {legalStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.legalReference")}</label>
            <input
              type="text"
              value={legalNotes}
              onChange={(e) => onChange({ legal_notes: e.target.value })}
              placeholder={t("round36.collectionTypeSelector.placeholders.legalReference")}
              style={inputStyle}
            />
          </div>
          <p style={{ marginTop: 12, fontSize: 11, color: "#8A8A8A", fontStyle: "italic", lineHeight: 1.5 }}>
            {t("round36.collectionTypeSelector.hints.inalienable")}
          </p>
        </div>
      )}

      {/* Dépôt collectionneur */}
      {showDepositPanel && (
        <div style={{ background: "#F4F4F2", border: "1px solid #D8D6CF", padding: "20px 22px", marginBottom: 16 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1A1A1A", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#6B6B6B" }}>◇</span>
            {t("round36.collectionTypeSelector.panels.depositTitle")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.ownerType")}</label>
              <select value={deposit.owner_type || "a_clarifier"} onChange={(e) => handleDepositChange("owner_type", e.target.value)} style={inputStyle}>
                {ownerTypeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.ownerName")}</label>
              <input type="text" value={deposit.owner_name || ""} onChange={(e) => handleDepositChange("owner_name", e.target.value)} placeholder={t("round36.collectionTypeSelector.placeholders.ownerName")} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.contractStart")}</label>
              <input type="date" value={deposit.contract_start_date || ""} onChange={(e) => handleDepositChange("contract_start_date", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.contractEnd")}</label>
              <input type="date" value={deposit.contract_end_date || ""} onChange={(e) => handleDepositChange("contract_end_date", e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.conditions")}</label>
            <textarea value={deposit.conditions_notes || ""} onChange={(e) => handleDepositChange("conditions_notes", e.target.value)} rows={2} placeholder={t("round36.collectionTypeSelector.placeholders.conditions")} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <p style={{ marginTop: 12, fontSize: 11, color: "#8A8A8A", fontStyle: "italic", lineHeight: 1.5 }}>
            {t("round36.collectionTypeSelector.hints.deposit")}
          </p>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PANNEAUX CONDITIONNELS — FONDATION
          Jamais rendus si isFoundation=false
      ══════════════════════════════════════════════════════════════════════ */}

      {/* Statut juridique — foundation_deposit ou foundation_donation */}
      {showFoundationLegal && (
        <div style={{ background: "#F0F4F8", border: "1px solid #A8BDD9", padding: "20px 22px", marginBottom: 16 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1A1A1A", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#1C3A5E" }}>◈</span>
            {t("round36.collectionTypeSelector.panels.foundationLegalTitle")}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.legalStatus")}</label>
            <select value={legalStatus} onChange={(e) => onChange({ legal_status: e.target.value })} style={inputStyle}>
              {legalStatusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.conventionReference")}</label>
            <input
              type="text"
              value={legalNotes}
              onChange={(e) => onChange({ legal_notes: e.target.value })}
              placeholder={t("round36.collectionTypeSelector.placeholders.conventionReference")}
              style={inputStyle}
            />
          </div>
          <p style={{ marginTop: 12, fontSize: 11, color: "#8A8A8A", fontStyle: "italic", lineHeight: 1.5 }}>
            {collectionType === "foundation_deposit"
              ? t("round36.collectionTypeSelector.hints.foundationDeposit")
              : t("round36.collectionTypeSelector.hints.foundationDonation")
            }
          </p>
        </div>
      )}

      {/* Tiers propriétaire — foundation_deposit ou foundation_founder */}
      {showThirdPartyPanel && (
        <div style={{ background: "#F8F6F0", border: "1px solid #D9C9A8", padding: "20px 22px", marginBottom: 16 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1A1A1A", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#7A5C00" }}>◈</span>
            {collectionType === "foundation_founder" ? t("round36.collectionTypeSelector.panels.founderLender") : t("round36.collectionTypeSelector.panels.realOwner")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.partyType")}</label>
              <select value={thirdParty.party_type || "a_clarifier"} onChange={(e) => handleThirdPartyChange("party_type", e.target.value)} style={inputStyle}>
                {foundationPartyTypeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.partyName")}</label>
              <input type="text" value={thirdParty.party_name || ""} onChange={(e) => handleThirdPartyChange("party_name", e.target.value)} placeholder={t("round36.collectionTypeSelector.placeholders.partyName")} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.partyStart")}</label>
              <input type="date" value={thirdParty.start_date || ""} onChange={(e) => handleThirdPartyChange("start_date", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.partyEnd")}</label>
              <input type="date" value={thirdParty.end_date || ""} onChange={(e) => handleThirdPartyChange("end_date", e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.contractReference")}</label>
              <input type="text" value={thirdParty.contract_ref || ""} onChange={(e) => handleThirdPartyChange("contract_ref", e.target.value)} placeholder={t("round36.collectionTypeSelector.placeholders.contractReference")} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t("round36.collectionTypeSelector.fields.insuredBy")}</label>
              <select value={thirdParty.insured_by || ""} onChange={(e) => handleThirdPartyChange("insured_by", e.target.value)} style={inputStyle}>
                <option value="">{t("round36.collectionTypeSelector.insuredBy.unspecified")}</option>
                <option value="fondation">{t("round36.collectionTypeSelector.insuredBy.foundation")}</option>
                <option value="proprietaire">{t("round36.collectionTypeSelector.insuredBy.owner")}</option>
                <option value="partage">{t("round36.collectionTypeSelector.insuredBy.shared")}</option>
              </select>
            </div>
          </div>
          <p style={{ marginTop: 4, fontSize: 11, color: "#8A8A8A", fontStyle: "italic", lineHeight: 1.5 }}>
            {t("round36.collectionTypeSelector.hints.foundation")}
          </p>
        </div>
      )}
    </div>
  );
}